import { useTranslation } from 'react-i18next';
import { Button, StatusBadge, type StatusBadgeStatus } from '@xxtract/ui';
import { isIntakeColumnReviewed } from '../review.utils';
import { TargetFieldSelect } from './TargetFieldSelect';
import type { Gs1FieldOption, IntakeColumnMappingProposal } from '../intake.api';

const CONFIDENCE_BADGE_STATUS: Record<IntakeColumnMappingProposal['confidenceClass'], StatusBadgeStatus> = {
  high: 'confidence-high',
  medium: 'confidence-medium',
  low: 'confidence-low',
};

export interface ColumnRowProps {
  column: IntakeColumnMappingProposal;
  isActive: boolean;
  gs1Options: Gs1FieldOption[];
  disabled?: boolean;
  onSelect: () => void;
  onSetField: (field: string | null) => void;
  onConfirm: () => void;
  onIgnore: () => void;
}

export function ColumnRow({ column, isActive, gs1Options, disabled, onSelect, onSetField, onConfirm, onIgnore }: ColumnRowProps) {
  const { t } = useTranslation();
  const reviewed = isIntakeColumnReviewed(column);

  return (
    <li
      data-testid={`intake-column-row-${column.header}`}
      className={`rounded-[var(--radius-card)] border p-3 ${isActive ? 'border-[var(--secondary)] bg-[var(--secondary-light)]' : 'border-[var(--border)]'}`}
    >
      <button
        type="button"
        data-testid={`intake-column-select-${column.header}`}
        onClick={onSelect}
        aria-pressed={isActive}
        className="flex w-full items-center justify-between gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]"
      >
        <span className="truncate text-sm font-medium text-[var(--foreground)]">{column.header}</span>
        <StatusBadge label={t(`intake.detail.reviewReady.confidenceClass.${column.confidenceClass}`)} status={CONFIDENCE_BADGE_STATUS[column.confidenceClass]} />
      </button>

      {column.confidenceClass === 'medium' && !reviewed ? (
        <p role="status" className="mt-1 text-xs text-[var(--foreground-muted)]">
          {t('intake.review.mediumQuestion', { field: column.suggestedField ?? '' })}
        </p>
      ) : null}

      <div className="mt-2 flex flex-wrap items-end gap-2">
        <TargetFieldSelect options={gs1Options} value={column.suggestedField} onChange={onSetField} disabled={disabled || column.ignored} />
        {column.confidenceClass === 'medium' && !column.confirmed && !column.ignored ? (
          <Button size="sm" variant="secondary" data-testid={`intake-column-confirm-${column.header}`} disabled={disabled} onClick={onConfirm}>
            {t('intake.review.confirmCta')}
          </Button>
        ) : null}
        <Button
          size="sm"
          variant={column.ignored ? 'default' : 'outline'}
          data-testid={`intake-column-ignore-${column.header}`}
          disabled={disabled}
          onClick={onIgnore}
        >
          {column.ignored ? t('intake.review.ignoredLabel') : t('intake.review.ignoreCta')}
        </Button>
      </div>
    </li>
  );
}
